import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../services/api';
import '../styles/CreateAnnonce.css';

const EditProduct = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    original_price: '',
    category_id: '',
    subcategory_id: '',
    size: '',
    brand: '',
    model: '',
    year: '',
    color: '',
    material: '',
    condition: 'bon',
    street: '',
    city: '',
    postal_code: '',
    shipping_available: false,
    shipping_price: '',
    local_pickup: false,
    // Vehicle fields
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_mileage: '',
    vehicle_fuel: '',
    vehicle_transmission: '',
    vehicle_power_cv: '',
    vehicle_displacement: '',
    vehicle_doors: '',
    vehicle_seats: '',
    vehicle_drive: '',
    vehicle_color_interior: '',
    vehicle_inspection_date: '',
    vehicle_vin: ''
  });
  const [mainCategories, setMainCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [primaryImagePath, setPrimaryImagePath] = useState('');
  const [savingImage, setSavingImage] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    fetchData();
  }, [id]);

  // Mettre a jour les sous-categories quand la categorie principale change
  useEffect(() => {
    if (formData.category_id && mainCategories.length > 0) {
      const selectedCategory = mainCategories.find(c => c.id.toString() === formData.category_id.toString());
      if (selectedCategory && selectedCategory.subcategories) {
        setSubcategories(selectedCategory.subcategories);
      } else {
        setSubcategories([]);
      }
    } else {
      setSubcategories([]);
    }
  }, [formData.category_id, mainCategories]);

  const fetchData = async () => {
    try {
      const [productRes, categoriesRes, imagesRes] = await Promise.all([
        productsAPI.getById(id),
        productsAPI.getCategoriesHierarchy(),
        productsAPI.getImages(id)
      ]);

      const product = productRes.data;
      setMainCategories(categoriesRes.data);

      // Charger les images avec leur statut is_primary
      const images = imagesRes.data || [];
      setExistingImages(images);
      const primaryImg = images.find(img => img.is_primary === 1);
      if (primaryImg) {
        setPrimaryImagePath(primaryImg.image_path);
      } else if (images.length > 0) {
        setPrimaryImagePath(images[0].image_path);
      }

      // Trouver la categorie principale si le produit a une sous-categorie
      let mainCategoryId = product.category_id || '';
      let subCategoryId = '';

      // Chercher si la categorie du produit est une sous-categorie
      for (const mainCat of categoriesRes.data) {
        if (mainCat.id.toString() === product.category_id?.toString()) {
          mainCategoryId = mainCat.id;
          break;
        }
        if (mainCat.subcategories) {
          const foundSub = mainCat.subcategories.find(
            sub => sub.id.toString() === product.category_id?.toString()
          );
          if (foundSub) {
            mainCategoryId = mainCat.id;
            subCategoryId = foundSub.id;
            break;
          }
        }
      }

      setFormData({
        title: product.title || '',
        description: product.description || '',
        price: product.price || '',
        original_price: product.original_price || '',
        category_id: mainCategoryId.toString(),
        subcategory_id: subCategoryId ? subCategoryId.toString() : '',
        size: product.size || '',
        brand: product.brand || '',
        model: product.model || '',
        year: product.year || '',
        color: product.color || '',
        material: product.material || '',
        condition: product.condition || 'bon',
        street: product.street || '',
        city: product.city || '',
        postal_code: product.postal_code || '',
        shipping_available: product.shipping_available === 1 || product.shipping_available === true,
        shipping_price: product.shipping_price ? String(product.shipping_price) : '',
        local_pickup: product.local_pickup === 1 || product.local_pickup === true,
        // Vehicle fields
        vehicle_make: product.vehicle_make || '',
        vehicle_model: product.vehicle_model || '',
        vehicle_year: product.vehicle_year || '',
        vehicle_mileage: product.vehicle_mileage || '',
        vehicle_fuel: product.vehicle_fuel || '',
        vehicle_transmission: product.vehicle_transmission || '',
        vehicle_power_cv: product.vehicle_power_cv || '',
        vehicle_displacement: product.vehicle_displacement || '',
        vehicle_doors: product.vehicle_doors || '',
        vehicle_seats: product.vehicle_seats || '',
        vehicle_drive: product.vehicle_drive || '',
        vehicle_color_interior: product.vehicle_color_interior || '',
        vehicle_inspection_date: product.vehicle_inspection_date ? product.vehicle_inspection_date.split('T')[0] : '',
        vehicle_vin: product.vehicle_vin || ''
      });

    } catch (err) {
      console.error('Erreur chargement:', err);
      setError('Erreur lors du chargement de l\'article');
    } finally {
      setLoading(false);
    }
  };

  // Validation des champs
  const validateField = (name, value) => {
    switch (name) {
      case 'title':
        if (!value || value.trim() === '') return 'Le titre est obligatoire';
        if (value.length < 5) return 'Le titre doit contenir au moins 5 caracteres';
        if (value.length > 200) return 'Le titre ne peut pas depasser 200 caracteres';
        return '';
      case 'description':
        if (!value || value.trim() === '') return 'La description est obligatoire';
        if (value.length < 10) return 'La description doit contenir au moins 10 caracteres';
        return '';
      case 'price':
        if (!value || value === '') return 'Le prix est obligatoire';
        if (isVehicle && parseFloat(value) < 500) return t('vehicle.minPrice');
        if (!isVehicle && parseFloat(value) < 0.50) return 'Le prix minimum est de 0.50 CHF';
        return '';
      case 'category_id':
        if (!value || value === '') return 'La categorie est obligatoire';
        return '';
      default:
        return '';
    }
  };

  // Validation complete du formulaire
  const validateForm = () => {
    const errors = {};

    const titleError = validateField('title', formData.title);
    if (titleError) errors.title = titleError;

    const descError = validateField('description', formData.description);
    if (descError) errors.description = descError;

    const priceError = validateField('price', formData.price);
    if (priceError) errors.price = priceError;

    const categoryError = validateField('category_id', formData.category_id);
    if (categoryError) errors.category_id = categoryError;

    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (touched[name]) {
      const error = validateField(name, newValue);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Definir la photo principale
  const handleSetPrimaryImage = async (imagePath) => {
    if (imagePath === primaryImagePath) return;

    setSavingImage(true);
    try {
      await productsAPI.setPrimaryImage(id, imagePath);
      setPrimaryImagePath(imagePath);
      // Mettre a jour l'ordre des images dans l'etat local
      setExistingImages(prev => {
        const updated = prev.map(img => ({
          ...img,
          is_primary: img.image_path === imagePath ? 1 : 0
        }));
        return updated.sort((a, b) => b.is_primary - a.is_primary);
      });
    } catch (err) {
      console.error('Erreur changement photo principale:', err);
      setError('Erreur lors du changement de la photo principale');
    } finally {
      setSavingImage(false);
    }
  };

  // Determiner si c'est la categorie voitures
  const isVehicleCategory = () => {
    const selectedCat = mainCategories.find(c => c.id.toString() === formData.category_id?.toString());
    return selectedCat && selectedCat.slug === 'voitures';
  };

  const isVehicle = isVehicleCategory();

  // Liste des marques populaires
  const carMakes = [
    'Audi', 'BMW', 'Citroen', 'Dacia', 'Fiat', 'Ford', 'Honda', 'Hyundai',
    'Kia', 'Land Rover', 'Mazda', 'Mercedes-Benz', 'Mini', 'Mitsubishi',
    'Nissan', 'Opel', 'Peugeot', 'Porsche', 'Renault', 'Seat', 'Skoda',
    'Subaru', 'Suzuki', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo'
  ];

  // Calculer le prix total avec frais
  const calculateTotalPrice = () => {
    if (!formData.price || formData.price <= 0) return null;
    const price = parseFloat(formData.price);
    if (isVehicle) {
      return { price, insurance: 0, commission: 0, total: price, isVehicle: true };
    }
    const insurance = 1.5;
    const commission = price * 0.02;
    const total = price + insurance + commission;
    return { price, insurance, commission, total, isVehicle: false };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    setTouched({
      title: true,
      description: true,
      price: true,
      category_id: true
    });

    const errors = validateForm();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError('Veuillez corriger les erreurs ci-dessous');
      return;
    }

    setSaving(true);

    try {
      // Preparer les donnees a envoyer
      const dataToSend = {
        title: formData.title,
        description: formData.description,
        price: formData.price,
        condition: formData.condition,
        category_id: formData.subcategory_id || formData.category_id,
        original_price: formData.original_price || null,
        brand: formData.brand || null,
        model: formData.model || null,
        year: formData.year || null,
        color: formData.color || null,
        material: formData.material || null,
        size: formData.size || null,
        street: formData.street || null,
        city: formData.city || null,
        postal_code: formData.postal_code || null,
        shipping_available: isVehicle ? 0 : (formData.shipping_available ? 1 : 0),
        shipping_price: isVehicle ? 0 : (formData.shipping_available && formData.shipping_price ? formData.shipping_price : 0),
        local_pickup: isVehicle ? 1 : (formData.local_pickup ? 1 : 0)
      };

      // Vehicle fields
      if (isVehicle) {
        dataToSend.vehicle_make = formData.vehicle_make || null;
        dataToSend.vehicle_model = formData.vehicle_model || null;
        dataToSend.vehicle_year = formData.vehicle_year || null;
        dataToSend.vehicle_mileage = formData.vehicle_mileage || null;
        dataToSend.vehicle_fuel = formData.vehicle_fuel || null;
        dataToSend.vehicle_transmission = formData.vehicle_transmission || null;
        dataToSend.vehicle_power_cv = formData.vehicle_power_cv || null;
        dataToSend.vehicle_displacement = formData.vehicle_displacement || null;
        dataToSend.vehicle_doors = formData.vehicle_doors || null;
        dataToSend.vehicle_seats = formData.vehicle_seats || null;
        dataToSend.vehicle_drive = formData.vehicle_drive || null;
        dataToSend.vehicle_color_interior = formData.vehicle_color_interior || null;
        dataToSend.vehicle_inspection_date = formData.vehicle_inspection_date || null;
        dataToSend.vehicle_vin = formData.vehicle_vin || null;
      }

      await productsAPI.update(id, dataToSend);
      navigate('/my-products');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la mise a jour');
      setSaving(false);
    }
  };

  const getFieldClass = (fieldName) => {
    if (fieldErrors[fieldName]) return 'field-error';
    if (touched[fieldName] && !fieldErrors[fieldName]) return 'field-valid';
    return '';
  };

  // Determiner si c'est une categorie vestimentaire (pour afficher le champ taille)
  const isClothingCategory = () => {
    const clothingCategories = ['femme', 'homme', 'enfant'];
    const selectedCat = mainCategories.find(c => c.id.toString() === formData.category_id);
    return selectedCat && clothingCategories.includes(selectedCat.slug);
  };

  // Determiner si c'est une categorie tech (pour afficher modele/annee)
  const isTechCategory = () => {
    const techCategories = ['informatique', 'jeux-video', 'electromenager'];
    const selectedCat = mainCategories.find(c => c.id.toString() === formData.category_id);
    return selectedCat && techCategories.includes(selectedCat.slug);
  };

  const priceDetails = calculateTotalPrice();

  if (loading) {
    return <div className="loading-container"><p>{t('common.loading')}</p></div>;
  }

  return (
    <div className="create-annonce-container">
      <div className="create-annonce-card">
        <h2>{t('product.editTitle')}</h2>
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          {/* Titre */}
          <div className={`form-group ${getFieldClass('title')}`}>
            <label>{t('product.titleLabel')}</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              onBlur={handleBlur}
              minLength="5"
              maxLength="200"
              placeholder={t('product.titlePlaceholder')}
              className={fieldErrors.title ? 'input-error' : ''}
            />
            {fieldErrors.title && <span className="error-text">{fieldErrors.title}</span>}
          </div>

          {/* Categorie principale */}
          <div className={`form-group ${getFieldClass('category_id')}`}>
            <label>{t('product.categoryLabel')}</label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.category_id ? 'input-error' : ''}
            >
              <option value="">{t('product.categoryPlaceholder')}</option>
              {mainCategories.map(cat => (
                <option key={cat.id} value={cat.id}>{t('categories.' + cat.slug, cat.name)}</option>
              ))}
            </select>
            {fieldErrors.category_id && <span className="error-text">{fieldErrors.category_id}</span>}
          </div>

          {/* Sous-categorie */}
          {subcategories.length > 0 && (
            <div className="form-group">
              <label>{isVehicle ? t('vehicle.bodyType') : t('product.subcategoryLabel')}</label>
              <select
                name="subcategory_id"
                value={formData.subcategory_id}
                onChange={handleChange}
              >
                <option value="">{t('product.subcategoryPlaceholder')}</option>
                {subcategories.map(sub => (
                  <option key={sub.id} value={sub.id}>{t('categories.' + sub.slug, sub.name)}</option>
                ))}
              </select>
            </div>
          )}

          {/* === VEHICLE-SPECIFIC FIELDS === */}
          {isVehicle && (
            <>
              {/* Section 1: Identite du vehicule */}
              <div className="vehicle-form-section">
                <h3 className="vehicle-section-title">
                  <span className="vehicle-section-icon">&#128663;</span>
                  {t('vehicle.sectionIdentity')}
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.make')} *</label>
                    <select
                      name="vehicle_make"
                      value={formData.vehicle_make}
                      onChange={handleChange}
                    >
                      <option value="">{t('vehicle.selectMake')}</option>
                      {carMakes.map(make => (
                        <option key={make} value={make}>{make}</option>
                      ))}
                      <option value="Autre">{t('vehicle.otherMake')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.model')}</label>
                    <input
                      type="text"
                      name="vehicle_model"
                      value={formData.vehicle_model}
                      onChange={handleChange}
                      placeholder={t('vehicle.modelPlaceholder')}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.year')}</label>
                    <input
                      type="number"
                      name="vehicle_year"
                      value={formData.vehicle_year}
                      onChange={handleChange}
                      min="1950"
                      max={new Date().getFullYear() + 1}
                      placeholder={t('vehicle.yearPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.mileage')}</label>
                    <input
                      type="number"
                      name="vehicle_mileage"
                      value={formData.vehicle_mileage}
                      onChange={handleChange}
                      min="0"
                      placeholder={t('vehicle.mileagePlaceholder')}
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Motorisation */}
              <div className="vehicle-form-section">
                <h3 className="vehicle-section-title">
                  <span className="vehicle-section-icon">&#9881;</span>
                  {t('vehicle.sectionEngine')}
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.fuel')}</label>
                    <select
                      name="vehicle_fuel"
                      value={formData.vehicle_fuel}
                      onChange={handleChange}
                    >
                      <option value="">{t('vehicle.selectFuel')}</option>
                      <option value="essence">{t('vehicle.fuelEssence')}</option>
                      <option value="diesel">{t('vehicle.fuelDiesel')}</option>
                      <option value="hybride">{t('vehicle.fuelHybride')}</option>
                      <option value="electrique">{t('vehicle.fuelElectrique')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.transmission')}</label>
                    <select
                      name="vehicle_transmission"
                      value={formData.vehicle_transmission}
                      onChange={handleChange}
                    >
                      <option value="">{t('vehicle.selectTransmission')}</option>
                      <option value="manuelle">{t('vehicle.transmissionManuelle')}</option>
                      <option value="automatique">{t('vehicle.transmissionAutomatique')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.power')}</label>
                    <input
                      type="number"
                      name="vehicle_power_cv"
                      value={formData.vehicle_power_cv}
                      onChange={handleChange}
                      min="0"
                      placeholder={t('vehicle.powerPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.displacement')}</label>
                    <input
                      type="number"
                      name="vehicle_displacement"
                      value={formData.vehicle_displacement}
                      onChange={handleChange}
                      min="0"
                      placeholder={t('vehicle.displacementPlaceholder')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('vehicle.drive')}</label>
                  <select
                    name="vehicle_drive"
                    value={formData.vehicle_drive}
                    onChange={handleChange}
                  >
                    <option value="">-</option>
                    <option value="avant">{t('vehicle.driveFront')}</option>
                    <option value="arriere">{t('vehicle.driveRear')}</option>
                    <option value="4x4">{t('vehicle.drive4x4')}</option>
                  </select>
                </div>
              </div>

              {/* Section 3: Habitacle */}
              <div className="vehicle-form-section">
                <h3 className="vehicle-section-title">
                  <span className="vehicle-section-icon">&#128186;</span>
                  {t('vehicle.sectionInterior')}
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.doors')}</label>
                    <select
                      name="vehicle_doors"
                      value={formData.vehicle_doors}
                      onChange={handleChange}
                    >
                      <option value="">-</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.seats')}</label>
                    <select
                      name="vehicle_seats"
                      value={formData.vehicle_seats}
                      onChange={handleChange}
                    >
                      <option value="">-</option>
                      <option value="2">2</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="7">7</option>
                      <option value="9">9</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.exteriorColor')}</label>
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleChange}
                      placeholder={t('vehicle.colorPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.interiorColor')}</label>
                    <input
                      type="text"
                      name="vehicle_color_interior"
                      value={formData.vehicle_color_interior}
                      onChange={handleChange}
                      placeholder={t('vehicle.interiorColorPlaceholder')}
                    />
                  </div>
                </div>
              </div>

              {/* Section 4: Etat & controle */}
              <div className="vehicle-form-section">
                <h3 className="vehicle-section-title">
                  <span className="vehicle-section-icon">&#128196;</span>
                  {t('vehicle.sectionDocuments')}
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('vehicle.inspection')}</label>
                    <input
                      type="date"
                      name="vehicle_inspection_date"
                      value={formData.vehicle_inspection_date}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('vehicle.vin')}</label>
                    <input
                      type="text"
                      name="vehicle_vin"
                      value={formData.vehicle_vin}
                      onChange={handleChange}
                      placeholder={t('vehicle.vinPlaceholder')}
                      maxLength="20"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* === STANDARD PRODUCT FIELDS (hidden for vehicles) === */}
          {!isVehicle && (
            <>
              {/* Marque et Modele */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.brand')}</label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    placeholder={t('product.brandPlaceholder')}
                  />
                </div>

                {isTechCategory() && (
                  <div className="form-group">
                    <label>{t('common.model')}</label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      placeholder={t('product.modelPlaceholder')}
                    />
                  </div>
                )}
              </div>

              {/* Taille (pour vetements) */}
              {isClothingCategory() && (
                <div className="form-group">
                  <label>{t('common.size')}</label>
                  <input
                    type="text"
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    placeholder={t('product.sizePlaceholder')}
                  />
                </div>
              )}

              {/* Couleur et Matiere */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.color')}</label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder={t('product.colorPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('product.materialLabel')}</label>
                  <input
                    type="text"
                    name="material"
                    value={formData.material}
                    onChange={handleChange}
                    placeholder={t('product.materialPlaceholder')}
                  />
                </div>
              </div>

              {/* Annee (pour tech) */}
              {isTechCategory() && (
                <div className="form-group">
                  <label>{t('common.year')}</label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    min="1990"
                    max={new Date().getFullYear()}
                    placeholder={t('product.yearPlaceholder')}
                  />
                </div>
              )}
            </>
          )}

          {/* Etat */}
          <div className="form-group">
            <label>{t('product.conditionLabel')}</label>
            <select
              name="condition"
              value={formData.condition}
              onChange={handleChange}
            >
              <option value="neuf_etiquette">{t('product.conditionNeuf')}</option>
              <option value="neuf_sans_etiquette">{t('product.conditionNeufSans')}</option>
              <option value="tres_bon">{t('product.conditionTresBon')}</option>
              <option value="bon">{t('product.conditionBon')}</option>
              <option value="satisfaisant">{t('product.conditionSatisfaisant')}</option>
            </select>
          </div>

          {/* Prix */}
          <div className="form-row">
            <div className={`form-group ${getFieldClass('price')}`}>
              <label>{t('product.priceLabel')}</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                onBlur={handleBlur}
                min={isVehicle ? '500' : '0.50'}
                step={isVehicle ? '100' : '0.50'}
                placeholder={isVehicle ? t('vehicle.pricePlaceholder') : t('product.pricePlaceholder')}
                className={fieldErrors.price ? 'input-error' : ''}
              />
              {fieldErrors.price && <span className="error-text">{fieldErrors.price}</span>}
            </div>

            {!isVehicle && (
              <div className="form-group">
                <label>{t('product.originalPriceLabel')}</label>
                <input
                  type="number"
                  name="original_price"
                  value={formData.original_price}
                  onChange={handleChange}
                  min="0"
                  step="0.50"
                  placeholder={t('product.originalPricePlaceholder')}
                />
                <small className="field-hint">{t('product.originalPriceNote')}</small>
              </div>
            )}
          </div>

          {/* Affichage du prix total avec frais (masque pour voitures) */}
          {priceDetails && !priceDetails.isVehicle && (
            <div className="price-breakdown">
              <h4>{t('product.priceDetail')}</h4>
              <div className="price-line">
                <span>{t('product.itemPrice', { price: priceDetails.price.toFixed(2) }).split(':')[0]}</span>
                <span>{priceDetails.price.toFixed(2)} CHF</span>
              </div>
              <div className="price-line">
                <span>{t('product.buyerProtection')}</span>
                <span>{priceDetails.insurance.toFixed(2)} CHF</span>
              </div>
              <div className="price-line">
                <span>{t('product.serviceFee')}</span>
                <span>{priceDetails.commission.toFixed(2)} CHF</span>
              </div>
              <div className="price-line total">
                <span>{t('product.totalPrice')}</span>
                <span>{priceDetails.total.toFixed(2)} CHF</span>
              </div>
            </div>
          )}

          {/* Description */}
          <div className={`form-group ${getFieldClass('description')}`}>
            <label>{t('product.descriptionLabel')}</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              onBlur={handleBlur}
              minLength="10"
              rows={isVehicle ? 8 : 5}
              placeholder={isVehicle ? t('vehicle.descriptionPlaceholder') : t('product.descriptionPlaceholder')}
              className={fieldErrors.description ? 'input-error' : ''}
            />
            {fieldErrors.description && <span className="error-text">{fieldErrors.description}</span>}
          </div>

          {/* Images existantes */}
          {existingImages.length > 0 && (
            <div className="form-group">
              <label>Photos {savingImage && <span className="saving-indicator">({t('common.savingChanges')})</span>}</label>
              <p className="preview-instruction">{t('product.clickToSetMain')}</p>
              <div className="preview-grid">
                {existingImages.map((img, index) => (
                  <div
                    key={index}
                    className={`preview-item ${img.image_path === primaryImagePath ? 'main-image' : ''}`}
                    onClick={() => handleSetPrimaryImage(img.image_path)}
                    style={{ cursor: savingImage ? 'wait' : 'pointer' }}
                  >
                    <img
                      src={`/uploads/thumbnails/${img.image_path}`}
                      onError={(e) => { if (e.target.src.includes('/thumbnails/')) e.target.src = `/uploads/${img.image_path}`; }}
                      alt={`Image ${index + 1}`}
                    />
                    {img.image_path === primaryImagePath && (
                      <span className="main-badge">{t('product.mainPhoto')}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Localisation */}
          <div className="links-section">
            <h3>{t('product.locationLabel')}</h3>
            <div className="form-group">
              <label>{t('product.streetLabel')}</label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                placeholder={t('product.streetPlaceholder')}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('common.postalCode')}</label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder={t('product.postalCodePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('common.city')}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder={t('product.cityPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Options de livraison (masque pour voitures) */}
          {!isVehicle && (
            <div className="guest-list-section">
              <h3>{t('product.shippingLabel')}</h3>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="shipping_available"
                    checked={formData.shipping_available}
                    onChange={handleChange}
                  />
                  {t('product.shippingAvailable')}
                </label>
              </div>

              {formData.shipping_available && (
                <div className="form-group">
                  <label>{t('product.shippingCost')}</label>
                  <input
                    type="number"
                    name="shipping_price"
                    value={formData.shipping_price}
                    onChange={handleChange}
                    min="0"
                    step="0.50"
                    placeholder={t('product.shippingDefault')}
                  />
                </div>
              )}

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="local_pickup"
                    checked={formData.local_pickup}
                    onChange={handleChange}
                  />
                  {t('product.handDelivery')}
                </label>
              </div>
            </div>
          )}

          {/* Info remise en main propre pour voitures */}
          {isVehicle && (
            <div className="guest-list-section">
              <h3>{t('product.shippingLabel')}</h3>
              <p style={{ color: '#666', fontSize: '0.9em' }}>{t('vehicle.pickupOnly')}</p>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t('product.saving') : t('product.saveButton')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/my-products')}
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProduct;
